import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Ruler, Plus, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import * as accountService from '../../../services/accountService';
import { useAuth } from '../../../contexts/AuthContext';
import ProgressChart from '../../shared/ProgressChart';

/**
 * Measurement Tracker Component
 * Body measurements input with chart visualization
 */
export default function MeasurementTracker({ planId }) {
  const { user } = useAuth();
  const [measurements, setMeasurements] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    entryDate: new Date().toISOString().split('T')[0],
    weight: '',
    bodyFat: '',
    chest: '',
    waist: '',
    hips: '',
    arms: '',
    thighs: '',
    notes: '',
  });

  useEffect(() => {
    if (planId && user) {
      loadMeasurements();
    }
  }, [planId, user]);

  const loadMeasurements = async () => {
    setIsLoading(true);
    try {
      const result = await accountService.getProgressEntries(user.id, {
        entryType: 'measurement',
      });
      if (result.success) {
        const planMeasurements = (result.data || []).filter(
          (e) => e.program_id === planId
        );
        setMeasurements(planMeasurements.sort((a, b) => 
          new Date(b.entry_date) - new Date(a.entry_date)
        ));
      }
    } catch (error) {
      console.error('Error loading measurements:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return;

    const measurementData = {
      weight: formData.weight ? parseFloat(formData.weight) : null,
      bodyFat: formData.bodyFat ? parseFloat(formData.bodyFat) : null,
      chest: formData.chest ? parseFloat(formData.chest) : null,
      waist: formData.waist ? parseFloat(formData.waist) : null,
      hips: formData.hips ? parseFloat(formData.hips) : null,
      arms: formData.arms ? parseFloat(formData.arms) : null,
      thighs: formData.thighs ? parseFloat(formData.thighs) : null,
    };

    try {
      const result = await accountService.addProgressEntry(user.id, {
        entryType: 'measurement',
        entryDate: formData.entryDate,
        data: measurementData,
        notes: formData.notes || null,
      });

      if (result.success) {
        setShowAddForm(false);
        setFormData({
          entryDate: new Date().toISOString().split('T')[0],
          weight: '',
          bodyFat: '',
          chest: '',
          waist: '',
          hips: '',
          arms: '',
          thighs: '',
          notes: '',
        });
        loadMeasurements();
      }
    } catch (error) {
      console.error('Error adding measurement:', error);
    }
  };

  const getChangeIndicator = (current, previous) => {
    if (!current || !previous) return null;
    const change = current - previous;
    if (change > 0) {
      return <TrendingUp className="w-4 h-4 text-green-600" />;
    } else if (change < 0) {
      return <TrendingDown className="w-4 h-4 text-red-600" />;
    }
    return <Minus className="w-4 h-4 text-gray-400" />;
  };

  // Prepare chart data
  const weightChartData = measurements
    .filter(m => m.data?.weight)
    .map(m => ({
      date: m.entry_date,
      value: m.data.weight,
    }))
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading measurements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Body Measurements</h2>
        <button
          onClick={() => setShowAddForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Add Measurement
        </button>
      </div>

      {/* Add Measurement Form */}
      {showAddForm && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-lg p-6"
        >
          <h3 className="text-xl font-bold text-gray-900 mb-4">Add Measurement</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date
              </label>
              <input
                type="date"
                value={formData.entryDate}
                onChange={(e) => setFormData({ ...formData, entryDate: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Weight (lbs)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Body Fat (%)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.bodyFat}
                  onChange={(e) => setFormData({ ...formData, bodyFat: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chest (inches)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.chest}
                  onChange={(e) => setFormData({ ...formData, chest: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Waist (inches)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.waist}
                  onChange={(e) => setFormData({ ...formData, waist: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Hips (inches)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.hips}
                  onChange={(e) => setFormData({ ...formData, hips: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Arms (inches)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={formData.arms}
                  onChange={(e) => setFormData({ ...formData, arms: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  placeholder="0.0"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Thighs (inches)
              </label>
              <input
                type="number"
                step="0.1"
                value={formData.thighs}
                onChange={(e) => setFormData({ ...formData, thighs: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="0.0"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="Additional notes..."
              />
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                Save Measurement
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddForm(false);
                  setFormData({
                    entryDate: new Date().toISOString().split('T')[0],
                    weight: '',
                    bodyFat: '',
                    chest: '',
                    waist: '',
                    hips: '',
                    arms: '',
                    thighs: '',
                    notes: '',
                  });
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Weight Chart */}
      {weightChartData.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Weight Progress</h3>
          <ProgressChart
            data={weightChartData}
            type="line"
            xKey="date"
            yKey="value"
            label="Weight (lbs)"
          />
        </div>
      )}

      {/* Measurements List */}
      {measurements.length > 0 ? (
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Measurement History</h3>
          <div className="space-y-4">
            {measurements.map((measurement, index) => {
              const previous = index < measurements.length - 1 ? measurements[index + 1] : null;
              const data = measurement.data || {};

              return (
                <div key={measurement.id} className="border-b border-gray-200 pb-4 last:border-0">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-gray-900">
                      {new Date(measurement.entry_date).toLocaleDateString('en-US', {
                        month: 'long',
                        day: 'numeric',
                        year: 'numeric',
                      })}
                    </h4>
                    {previous && (
                      <div className="flex items-center gap-1 text-sm text-gray-600">
                        {getChangeIndicator(data.weight, previous.data?.weight)}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {data.weight && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Weight</p>
                        <p className="font-medium text-gray-900">{data.weight} lbs</p>
                      </div>
                    )}
                    {data.bodyFat && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Body Fat</p>
                        <p className="font-medium text-gray-900">{data.bodyFat}%</p>
                      </div>
                    )}
                    {data.chest && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Chest</p>
                        <p className="font-medium text-gray-900">{data.chest}"</p>
                      </div>
                    )}
                    {data.waist && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Waist</p>
                        <p className="font-medium text-gray-900">{data.waist}"</p>
                      </div>
                    )}
                    {data.hips && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Hips</p>
                        <p className="font-medium text-gray-900">{data.hips}"</p>
                      </div>
                    )}
                    {data.arms && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Arms</p>
                        <p className="font-medium text-gray-900">{data.arms}"</p>
                      </div>
                    )}
                    {data.thighs && (
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Thighs</p>
                        <p className="font-medium text-gray-900">{data.thighs}"</p>
                      </div>
                    )}
                  </div>
                  {measurement.notes && (
                    <p className="text-sm text-gray-600 mt-2">{measurement.notes}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <Ruler className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-bold text-gray-900 mb-2">No measurements yet</h3>
          <p className="text-gray-600 mb-4">Start tracking your body measurements</p>
          <button
            onClick={() => setShowAddForm(true)}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors"
          >
            Add First Measurement
          </button>
        </div>
      )}
    </div>
  );
}
